import { ChangeDetectorRef, Component, OnInit, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { LogicielCaisseParametreAssociationsService } from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import {
  EntrepotApiRecord,
  LogicielCaisseEntrepotOption,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import { LogicielCaisseCaisseService } from 'app/services/logiciel-caisse/logiciel-caisse-caisse.service';
import { LogicielCaisseVendeurService } from 'app/services/logiciel-caisse/logiciel-caisse-vendeur.service';
import { LogicielCaisseSetupService } from 'app/services/logiciel-caisse/logiciel-caisse-setup.service';
import { readObservableError } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { ParametreNavService } from 'app/services/parametre/parametre-nav.service';

@Component({
  selector: 'app-logiciel-caisse-setup-wizard',
  templateUrl: './logiciel-caisse-setup-wizard.component.html',
  styleUrls: ['./logiciel-caisse-setup-wizard.component.scss'],
})
export class LogicielCaisseSetupWizardComponent implements OnInit, AfterViewInit {
  readonly steps = ['Établissement', 'Entrepôt', 'Caisse', 'Finalisation'];
  readonly stepHints = [
    'Votre magasin ou succursale.',
    'Sélectionnez un ou plusieurs entrepôts non rattachés, ou créez-en de nouveaux.',
    'Le poste où les ventes sont enregistrées.',
    'Vendeur et ticket (optionnel).',
  ];

  currentStep = 0;
  saving = false;
  addMode = false;
  skipVendeur = false;
  nextCaisseNumeroPreview: number | null = null;

  entrepotStepLoading = false;
  addingEntrepot = false;
  unlinkedEntrepotOptions: LogicielCaisseEntrepotOption[] = [];
  selectedEntrepotIds: string[] = [];
  caisseEntrepotId: string | null = null;
  private allEntrepotOptions: LogicielCaisseEntrepotOption[] = [];

  etablissementForm: UntypedFormGroup;
  entrepotForm: UntypedFormGroup;
  vendeurForm: UntypedFormGroup;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    readonly setupService: LogicielCaisseSetupService,
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly caisseService: LogicielCaisseCaisseService,
    private readonly vendeurService: LogicielCaisseVendeurService,
    private readonly parametreNav: ParametreNavService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.etablissementForm = this.fb.group({
      libelle: ['', Validators.required],
      code_etablissement: ['', Validators.required],
    });
    this.entrepotForm = this.fb.group({
      libelle: ['', Validators.required],
      code_entrepot: [''],
      ville: [''],
      pays: [''],
    });
    this.vendeurForm = this.fb.group({
      identifiant: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      nom: ['', Validators.required],
      prenom: ['', Validators.required],
      /** PIN caisse : chiffres uniquement, min. 6 (règle API /api/vendeurs/erp). */
      password: ['', [Validators.required, Validators.pattern(/^\d{6,}$/)]],
      telephone: [''],
    });
  }

  ngOnInit(): void {
    this.addMode = this.route.snapshot.queryParamMap.get('mode') === 'add';
    this.setupService.resetWizardContext();
    this.selectedEntrepotIds = [];
    this.caisseEntrepotId = null;
    this.allEntrepotOptions = [];
    this.unlinkedEntrepotOptions = [];
    this.prefillStep1();
    this.refreshFeatherIcons();
  }

  ngAfterViewInit(): void {
    this.parametreNav.markPageReady();
  }

  retour(): void {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
      this.refreshFeatherIcons();
      return;
    }
    void this.router.navigate(['/main/parametre/logiciel-caisse']);
  }

  onTimelineStepSelected(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex <= this.currentStep) {
      this.currentStep = stepIndex;
      if (stepIndex === 1) {
        this.loadEntrepotStep();
      }
      if (stepIndex === 2) {
        this.syncCaisseEntrepotSelection();
        this.loadNextCaisseNumeroPreview();
      }
      this.refreshFeatherIcons();
    }
  }

  get linkedEntrepotOptionsForCaisse(): LogicielCaisseEntrepotOption[] {
    const selected = new Set(this.selectedEntrepotIds);
    return this.allEntrepotOptions.filter((o) => selected.has(o.id));
  }

  get selectedEntrepotsSummary(): string {
    const labels = this.linkedEntrepotOptionsForCaisse.map((o) => o.label);
    if (!labels.length) {
      return 'Aucun entrepôt sélectionné';
    }
    return labels.join(', ');
  }

  createAndAddEntrepot(): void {
    if (this.entrepotForm.invalid) {
      this.entrepotForm.markAllAsTouched();
      return;
    }
    const payload = {
      libelle: String(this.entrepotForm.value.libelle).trim(),
      ville: String(this.entrepotForm.value.ville ?? '').trim(),
      pays: String(this.entrepotForm.value.pays ?? '').trim(),
      code_entrepot: String(this.entrepotForm.value.code_entrepot ?? '').trim() || null,
      is_reassort: false,
      is_globale: false,
    };
    this.addingEntrepot = true;
    this.entrepotService
      .createEntrepot(payload)
      .pipe(finalize(() => this.stopBusy('addingEntrepot')))
      .subscribe({
        next: (entrepot: EntrepotApiRecord) => {
          const id = String(entrepot.id);
          const label = payload.libelle || `Entrepôt ${id}`;
          this.allEntrepotOptions = [...this.allEntrepotOptions, { id, label }];
          this.unlinkedEntrepotOptions = [...this.unlinkedEntrepotOptions, { id, label }];
          if (!this.selectedEntrepotIds.includes(id)) {
            this.selectedEntrepotIds = [...this.selectedEntrepotIds, id];
          }
          this.prefillEntrepotFromEtab();
          this.toastr.success('Entrepôt créé et ajouté à la sélection.');
          this.refreshFeatherIcons();
        },
        error: (err: unknown) => {
          this.toastError(err, 'Création de l\'entrepôt impossible.');
        },
      });
  }

  removeSelectedEntrepot(id: string): void {
    this.selectedEntrepotIds = this.selectedEntrepotIds.filter((x) => x !== id);
    if (this.caisseEntrepotId === id) {
      this.caisseEntrepotId = this.selectedEntrepotIds[0] ?? null;
    }
  }

  goBack(): void {
    this.retour();
  }

  cancel(): void {
    void this.router.navigate(['/main/parametre/logiciel-caisse']);
  }

  next(): void {
    if (this.saving) {
      return;
    }
    switch (this.currentStep) {
      case 0:
        this.submitEtablissement();
        break;
      case 1:
        this.submitEntrepot();
        break;
      case 2:
        this.submitCaisse();
        break;
      case 3:
        this.finish();
        break;
    }
  }

  skipFinalisation(): void {
    this.skipVendeur = true;
    this.finish();
  }

  private prefillStep1(): void {
    this.associationsService.listEtablissements().subscribe({
      next: (items) => {
        const libelle = this.setupService.defaultEtablissementName();
        const code = this.setupService.suggestEtablissementCode(items);
        this.etablissementForm.patchValue({
          libelle: this.addMode ? `${libelle} — succursale` : libelle,
          code_etablissement: code,
        });
      },
      error: () => {
        this.etablissementForm.patchValue({
          libelle: this.setupService.defaultEtablissementName(),
          code_etablissement: '001',
        });
      },
    });
  }

  private prefillEntrepotFromEtab(): void {
    const ctx = this.setupService.getWizardContext();
    const libelle = ctx.etablissementLibelle?.trim() || 'Entrepôt';
    const suffix = this.selectedEntrepotIds.length > 0 ? ` ${this.selectedEntrepotIds.length + 1}` : '';
    this.entrepotForm.patchValue({
      libelle: `${libelle} — Stock${suffix}`,
      code_entrepot: '',
      ville: '',
      pays: '',
    });
  }

  private submitEtablissement(): void {
    if (this.etablissementForm.invalid) {
      this.etablissementForm.markAllAsTouched();
      return;
    }
    const body = {
      libelle: String(this.etablissementForm.value.libelle).trim(),
      code_etablissement: String(this.etablissementForm.value.code_etablissement).trim(),
    };
    this.saving = true;
    this.associationsService
      .createEtablissement(body)
      .pipe(finalize(() => this.stopBusy('saving')))
      .subscribe({
        next: (etab) => {
          this.setupService.patchWizardContext({
            etablissementId: etab.id,
            etablissementLibelle: etab.libelle,
          });
          this.prefillEntrepotFromEtab();
          this.loadEntrepotStep();
          this.currentStep = 1;
          this.toastr.success('Établissement créé.');
          this.refreshFeatherIcons();
        },
        error: (err: unknown) => {
          this.toastError(err, 'Création de l\'établissement impossible.');
        },
      });
  }

  private loadEntrepotStep(): void {
    const ctx = this.setupService.getWizardContext();
    const etablissementId = ctx.etablissementId;
    if (!etablissementId) {
      return;
    }
    this.entrepotStepLoading = true;
    forkJoin({
      options: this.entrepotService.getEntrepotOptions(),
      links: this.associationsService.listEtablissementEntrepotLinks(),
    }).subscribe({
      next: ({ options, links }) => {
        this.allEntrepotOptions = options;
        const linkedToOther = new Set(
          links
            .filter((l) => Number(l.etablissement_id) !== etablissementId)
            .map((l) => Number(l.entrepot_id))
        );
        const linkedToCurrent = links
          .filter((l) => Number(l.etablissement_id) === etablissementId)
          .map((l) => String(l.entrepot_id));

        this.unlinkedEntrepotOptions = options.filter(
          (o) => !linkedToOther.has(Number(o.id))
        );

        if (!this.selectedEntrepotIds.length && linkedToCurrent.length) {
          this.selectedEntrepotIds = [...linkedToCurrent];
        }

        this.entrepotStepLoading = false;
        this.cdr.markForCheck();
        this.refreshFeatherIcons();
      },
      error: (err: unknown) => {
        this.entrepotStepLoading = false;
        this.cdr.markForCheck();
        this.toastError(err, 'Chargement des entrepôts impossible.');
      },
    });
  }

  private syncCaisseEntrepotSelection(): void {
    if (!this.selectedEntrepotIds.length) {
      this.caisseEntrepotId = null;
      return;
    }
    if (!this.caisseEntrepotId || !this.selectedEntrepotIds.includes(this.caisseEntrepotId)) {
      this.caisseEntrepotId = this.selectedEntrepotIds[0];
    }
    const option = this.allEntrepotOptions.find((o) => o.id === this.caisseEntrepotId);
    if (option) {
      this.setupService.patchWizardContext({
        entrepotId: Number(this.caisseEntrepotId),
        entrepotLibelle: option.label,
      });
    }
  }

  onCaisseEntrepotChange(): void {
    this.syncCaisseEntrepotSelection();
    this.loadNextCaisseNumeroPreview();
  }

  private submitEntrepot(): void {
    const ctx = this.setupService.getWizardContext();
    if (!ctx.etablissementId) {
      this.toastr.error('Établissement manquant. Recommencez l’assistant.');
      this.currentStep = 0;
      return;
    }
    const entrepotIds = this.selectedEntrepotIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!entrepotIds.length) {
      this.toastr.warning('Sélectionnez au moins un entrepôt ou créez-en un nouveau.');
      return;
    }

    this.saving = true;
    const etablissementId = ctx.etablissementId;
    this.associationsService
      .setEtablissementEntrepots(etablissementId, entrepotIds)
      .pipe(finalize(() => this.stopBusy('saving')))
      .subscribe({
        next: () => {
          this.syncCaisseEntrepotSelection();
          this.currentStep = 2;
          this.loadNextCaisseNumeroPreview();
          const count = entrepotIds.length;
          this.toastr.success(
            count > 1
              ? `${count} entrepôts liés à l’établissement.`
              : 'Entrepôt lié à l’établissement.'
          );
          this.refreshFeatherIcons();
        },
        error: (err: unknown) => {
          this.toastError(err, 'Association établissement / entrepôt impossible.');
        },
      });
  }

  private loadNextCaisseNumeroPreview(): void {
    this.syncCaisseEntrepotSelection();
    const entrepotId = this.caisseEntrepotId ? Number(this.caisseEntrepotId) : null;
    if (!entrepotId) {
      this.nextCaisseNumeroPreview = null;
      return;
    }
    this.caisseService.getCaissesTableRows().subscribe({
      next: (rows) => {
        this.nextCaisseNumeroPreview = LogicielCaisseCaisseService.suggestNextNumero(
          entrepotId,
          rows
        );
      },
      error: () => {
        this.nextCaisseNumeroPreview = 1;
      },
    });
  }

  private submitCaisse(): void {
    this.syncCaisseEntrepotSelection();
    const entrepotId = this.caisseEntrepotId ? Number(this.caisseEntrepotId) : null;
    if (!entrepotId) {
      this.toastr.error('Entrepôt manquant. Recommencez l’assistant.');
      this.currentStep = 1;
      return;
    }
    this.saving = true;
    this.caisseService
      .createCaisse({ entrepot_id: entrepotId })
      .pipe(
        switchMap(() =>
          this.caisseService.getCaissesTableRows().pipe(
            switchMap((rows) => {
              const match = rows
                .filter(
                  (r) =>
                    Number(r.entrepot_id) === entrepotId &&
                    r._caisseStatutRaw !== 'archive'
                )
                .sort(
                  (a, b) => Number(b.numero_de_caisse ?? 0) - Number(a.numero_de_caisse ?? 0)
                )[0];
              const caisseId = match ? Number(match.id) : null;
              if (caisseId) {
                this.setupService.patchWizardContext({ caisseId });
              }
              return of(caisseId);
            })
          )
        ),
        finalize(() => this.stopBusy('saving'))
      )
      .subscribe({
        next: () => {
          this.currentStep = 3;
          this.toastr.success('Caisse créée.');
          this.refreshFeatherIcons();
        },
        error: (err: unknown) => {
          this.toastError(err, 'Création de la caisse impossible.');
        },
      });
  }

  private finish(): void {
    const ctx = this.setupService.getWizardContext();
    if (!ctx.caisseId) {
      this.toastr.error('Caisse manquante.');
      this.currentStep = 2;
      return;
    }

    if (this.skipVendeur || this.vendeurForm.invalid) {
      if (!this.skipVendeur && this.vendeurForm.invalid) {
        this.vendeurForm.markAllAsTouched();
        if (
          !this.vendeurForm.value.identifiant &&
          !this.vendeurForm.value.nom &&
          !this.vendeurForm.value.prenom &&
          !this.vendeurForm.value.password
        ) {
          this.skipVendeur = true;
        } else {
          const pwd = String(this.vendeurForm.value.password ?? '').trim();
          if (pwd && !/^\d{6,}$/.test(pwd)) {
            this.toastr.warning(
              'Le code PIN doit contenir uniquement des chiffres (6 minimum).',
              'Vendeur'
            );
          } else if (this.vendeurForm.get('identifiant')?.invalid) {
            this.toastr.warning('L’identifiant vendeur doit être un nombre.', 'Vendeur');
          } else {
            this.toastr.warning('Complétez les champs vendeur ou passez cette étape.', 'Vendeur');
          }
          return;
        }
      }
      this.navigateDone();
      return;
    }

    this.saving = true;
    const body = {
      identifiant: String(this.vendeurForm.value.identifiant).trim(),
      password: String(this.vendeurForm.value.password),
      nom: String(this.vendeurForm.value.nom).trim(),
      prenom: String(this.vendeurForm.value.prenom).trim(),
      telephone: String(this.vendeurForm.value.telephone ?? '').trim() || null,
    };

    this.vendeurService
      .createErp(body)
      .pipe(
        switchMap((vendeur) =>
          this.vendeurService.setVendeurCaisses(vendeur.id, [ctx.caisseId!]).pipe(
            switchMap(() => of(vendeur))
          )
        ),
        finalize(() => this.stopBusy('saving'))
      )
      .subscribe({
        next: () => {
          this.toastr.success('Vendeur créé et associé à la caisse.');
          this.navigateDone();
        },
        error: (err: unknown) => {
          this.toastError(err, 'Création du vendeur impossible.');
        },
      });
  }

  private navigateDone(): void {
    this.setupService.resetWizardContext();
    void this.router.navigate(['/main/parametre/logiciel-caisse']);
  }

  private stopBusy(flag: 'saving' | 'addingEntrepot'): void {
    this[flag] = false;
    this.cdr.markForCheck();
  }

  private toastError(err: unknown, fallback: string): void {
    this.toastr.error(readObservableError(err, fallback), 'Erreur');
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      const feather = (window as unknown as { feather?: { replace: () => void } }).feather;
      feather?.replace();
    }, 0);
  }
}
